// src/components/ui/ConfirmDialog.jsx
/**
 * ConfirmDialog — the ONE premium confirmation dialog for the whole app.
 *
 * Consistent look everywhere: glass backdrop, soft pop animation, variant-tinted
 * icon medallion, high-contrast readable text, and a primary/secondary button pair.
 * Renders into a body portal so it always sits above everything, and is fully
 * accessible (Escape to cancel, backdrop click to cancel, autofocus on confirm,
 * focus trap, scroll lock).
 *
 * COMPOSITION — medallion over title over message, centred. The medallion used
 * to sit in a column down the left, which pushed every word into a narrow band
 * on the right and read more lopsided the longer the message got. Pass
 * `align="left"` when the message is structured (a list, a callout) and only the
 * body should return to the left margin.
 *
 * Usage:
 *   import ConfirmDialog from '../ui/ConfirmDialog';
 *
 *   const [open, setOpen] = useState(false);
 *   <ConfirmDialog
 *     open={open}
 *     variant="warning"                 // 'warning' | 'danger' | 'success' | 'info'
 *     title="Replace existing entries?"
 *     message={<>You already have <b>1</b> entry…</>}
 *     confirmLabel="Yes, replace"
 *     cancelLabel="No, keep current"
 *     onConfirm={() => { …; setOpen(false); }}
 *     onCancel={() => setOpen(false)}
 *   />
 *
 * STYLING MODEL — everything static lives in ONE stylesheet injected once per
 * document (see injectStyles); only what actually varies per dialog, the four
 * variant colours, crosses over as CSS custom properties on the card. It used to
 * be ~20 inline style objects rebuilt on every render, which also meant no
 * :hover, no :focus-visible and no media queries could reach the buttons — which
 * is why a long confirm label broke the footer instead of stacking it.
 */

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { lockBodyScroll } from '../../utils/scrollLock';

/* Line icons rather than emoji: emoji render differently on every OS (and at a
   different optical weight from the surrounding UI), which is exactly what made
   the dialogs look less finished than the rest of the app. Call sites can still
   pass `icon` to override with anything, emoji included. */
const Glyph = ({ d, extra }) => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
    {extra}
  </svg>
);

/* Per-variant theming: accent bar, icon medallion, primary button + glow. */
const VARIANTS = {
  warning: {
    node: <Glyph d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                  extra={<><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>} />,
    ink: '#B45309',
    accent: 'linear-gradient(90deg,#f59e0b,#fbbf24)',
    medallion: 'linear-gradient(135deg,#fef3c7,#fde68a)',
    btn: 'linear-gradient(135deg,#10B981,#059669)',
    btnGlow: 'rgba(16,185,129,0.34)',
    wash: '#fffbeb',
  },
  danger: {
    node: <Glyph d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"
                  extra={<><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>} />,
    ink: '#B91C1C',
    accent: 'linear-gradient(90deg,#ef4444,#dc2626)',
    medallion: 'linear-gradient(135deg,#fee2e2,#fecaca)',
    btn: 'linear-gradient(135deg,#ef4444,#dc2626)',
    btnGlow: 'rgba(239,68,68,0.34)',
    wash: '#fef2f2',
  },
  success: {
    node: <Glyph d="M22 11.08V12a10 10 0 1 1-5.93-9.14" extra={<polyline points="22 4 12 14.01 9 11.01" />} />,
    ink: '#15803D',
    accent: 'linear-gradient(90deg,#22c55e,#10B981)',
    medallion: 'linear-gradient(135deg,#d1fae5,#a7f3d0)',
    btn: 'linear-gradient(135deg,#10B981,#059669)',
    btnGlow: 'rgba(16,185,129,0.34)',
    wash: '#f0fdf4',
  },
  info: {
    node: <Glyph d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"
                  extra={<><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>} />,
    ink: '#1D4ED8',
    accent: 'linear-gradient(90deg,#3B82F6,#2563eb)',
    medallion: 'linear-gradient(135deg,#dbeafe,#bfdbfe)',
    btn: 'linear-gradient(135deg,#3B82F6,#2563eb)',
    btnGlow: 'rgba(59,130,246,0.34)',
    wash: '#eff6ff',
  },
};

const STYLE_ID = 'cd-dialog-styles';

/* One stylesheet for every dialog in the app, added once and left there. The
   type scale is deliberately a step below the page's: a dialog is read in one
   glance at close range, so 15.5/13 reads as composed where 18/14 read as
   shouting — and the smaller the block, the less the modal has to grow to hold
   whatever a call site passes it. */
function injectStyles() {
  if (typeof document === 'undefined') return;
  /* REPLACED when the content differs, not skipped because a tag with this id
     exists. Bailing on the id alone means every edit made during an `npm start`
     session is invisible: hot-reload swaps in the new module while the sheet the
     previous one injected stays in the head, so a newly styled dialog renders
     with the old rules and the change looks like it did nothing. */
  const existing = document.getElementById(STYLE_ID);
  const s = existing || document.createElement('style');
  s.id = STYLE_ID;
  const css = `
#confirm-dialog-root .cd-ov{position:fixed;inset:0;z-index:100000;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(15,23,42,.55);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);font-family:'Inter',system-ui,-apple-system,sans-serif;animation:cd-fade .18s ease}
/* The card never grows past the viewport: a dialog carrying a real briefing (a
   list, a callout) used to run off the bottom of a short window with no way to
   reach its own buttons, because the overlay centres it and clips the overflow.
   The BODY scrolls instead, so the title and the footer stay put. */
/* A REAL border. The old one was rgba(255,255,255,.6) — white on a white card,
   so the dialog had no edge at all and bled into its own shadow. */
#confirm-dialog-root .cd-card{width:100%;max-width:404px;max-height:calc(100vh - 32px);display:flex;flex-direction:column;background:#fff;border-radius:18px;overflow:hidden;border:1px solid #e3e9f2;box-shadow:0 30px 72px rgba(15,23,42,.30),0 6px 18px rgba(15,23,42,.10);animation:cd-pop .22s cubic-bezier(.34,1.56,.64,1)}
@supports(height:100dvh){#confirm-dialog-root .cd-card{max-height:calc(100dvh - 32px)}}
#confirm-dialog-root .cd-accent{height:3px;flex-shrink:0;background:var(--cd-accent)}
/* CENTRED composition. The medallion used to hold a column down the left, which
   pushed every word of the dialog into a narrow band on the right — the longer
   the message, the more lopsided it read. Icon over title over message puts the
   text on the card's own centre line and gives it the full width. */
#confirm-dialog-root .cd-body{display:block;text-align:center;padding:24px 24px 18px;min-height:0;overflow-y:auto;overscroll-behavior:contain}
#confirm-dialog-root .cd-body::-webkit-scrollbar{width:8px}
#confirm-dialog-root .cd-body::-webkit-scrollbar-thumb{background:#d6dee8;border-radius:8px}
#confirm-dialog-root .cd-body::-webkit-scrollbar-track{background:transparent}
#confirm-dialog-root .cd-medal-wrap{display:flex;justify-content:center;margin-bottom:14px}
#confirm-dialog-root .cd-medal{position:relative;width:52px;height:52px;flex-shrink:0;border-radius:17px;display:flex;align-items:center;justify-content:center;font-size:24px;line-height:1;background:var(--cd-medal);color:var(--cd-ink);box-shadow:inset 0 1px 0 rgba(255,255,255,.85),0 8px 20px var(--cd-glow)}
#confirm-dialog-root .cd-medal svg{width:25px;height:25px}
/* A hairline halo in the variant's own colour — the detail that makes the
   medallion read as a mark rather than a tinted square. */
#confirm-dialog-root .cd-medal::after{content:'';position:absolute;inset:-6px;border-radius:23px;border:1.5px solid var(--cd-glow);opacity:.55}
#confirm-dialog-root .cd-title{margin:0;font-size:17px;font-weight:700;color:#0f172a;letter-spacing:-.15px;line-height:1.32;text-wrap:balance}
#confirm-dialog-root .cd-desc{margin-top:7px;font-size:13.2px;font-weight:400;color:#55647c;line-height:1.6;text-wrap:pretty;overflow-wrap:anywhere}
#confirm-dialog-root .cd-desc b,#confirm-dialog-root .cd-desc strong{color:#1e293b;font-weight:600}
/* Structured content (a list, a callout) reads left however the card is composed. */
#confirm-dialog-root .cd-card.cd-left .cd-desc{text-align:left}
/* ── HEADER layout (layout="header") ─────────────────────────────────────────
   For a dialog that briefs rather than asks: the medallion and title sit in a
   banded header with its own rule, and the message gets the card's FULL width
   underneath instead of being indented under the title. That is the distinction
   the centred layout cannot make — centring is right for a sentence, wrong for
   a callout and a checklist that each want a left margin to line up on. */
#confirm-dialog-root .cd-hd{position:relative;flex-shrink:0;display:flex;align-items:flex-start;gap:14px;padding:19px 20px 17px;border-bottom:1px solid #eef2f7;background:linear-gradient(115deg,#fff 52%,var(--cd-wash));overflow:hidden}
/* A soft disc of the variant's own tint, bled off the top-right corner. The one
   decorative move on the card — enough to stop the header reading as a plain bar. */
#confirm-dialog-root .cd-hd::after{content:'';position:absolute;top:-104px;right:-58px;width:216px;height:216px;border-radius:50%;background:var(--cd-medal);opacity:.34;pointer-events:none}
#confirm-dialog-root .cd-hd>*{position:relative;z-index:1}
#confirm-dialog-root .cd-hd-txt{min-width:0;flex:1;padding-top:3px}
#confirm-dialog-root .cd-hd .cd-title{font-size:18.5px}
#confirm-dialog-root .cd-sub{margin:4px 0 0;font-size:12.8px;font-weight:400;color:#64748b;line-height:1.5;text-wrap:pretty}
/* No halo here — in a header the medallion is already framed by the band. */
#confirm-dialog-root .cd-hdr .cd-medal{border-radius:15px;box-shadow:inset 0 1px 0 rgba(255,255,255,.85),0 5px 14px var(--cd-glow)}
#confirm-dialog-root .cd-hdr .cd-medal::after{display:none}
#confirm-dialog-root .cd-x{flex-shrink:0;width:32px;height:32px;margin:-2px -4px 0 0;border:none;border-radius:9px;background:transparent;color:#94a3b8;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:background .15s ease,color .15s ease}
#confirm-dialog-root .cd-x svg{width:19px;height:19px}
#confirm-dialog-root .cd-x:hover{background:#f1f5f9;color:#334155}
#confirm-dialog-root .cd-x:focus-visible{outline:3px solid rgba(59,130,246,.45);outline-offset:2px}
#confirm-dialog-root .cd-hdr .cd-body{text-align:left;padding:18px 20px 6px}
#confirm-dialog-root .cd-hdr .cd-desc{margin-top:0;font-size:13.2px}
#confirm-dialog-root .cd-card.cd-lg{max-width:568px}
/* The opt-out ("Don't show this again") belongs with the actions, not with the
   content it would opt out of — so the footer grows a left slot. */
#confirm-dialog-root .cd-foot-left{margin-right:auto;display:flex;align-items:center;min-width:0}
/* Centred to match the card. A right-hugged pair under centred text looked like
   it belonged to a different dialog. */
#confirm-dialog-root .cd-foot{display:flex;flex-shrink:0;gap:10px;justify-content:center;align-items:stretch;flex-wrap:wrap;padding:14px 24px 20px;border-top:1px solid #eef2f7;background:linear-gradient(180deg,#fff,#fafbfe)}
#confirm-dialog-root .cd-btn{font-family:inherit;font-size:13px;border-radius:10px;letter-spacing:.1px;line-height:1.35;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:7px;max-width:100%;transition:transform .18s ease,box-shadow .18s ease,filter .18s ease,background .18s ease,border-color .18s ease,color .18s ease}
#confirm-dialog-root .cd-btn:disabled{cursor:not-allowed}
/* A label may carry a leading icon. MUI icons default to 24px, which towers over
   13px button text — size them to the type, not to their own default. */
#confirm-dialog-root .cd-btn svg{width:17px;height:17px;flex-shrink:0}
#confirm-dialog-root .cd-btn:focus-visible{outline:3px solid rgba(59,130,246,.45);outline-offset:2px}
/* Ghost stays on one line — a two-word "Keep current" that wraps looks broken.
   The primary is the one allowed to run long, and it takes the remaining room. */
/* Min widths so a short label ("OK", "Cancel") still reads as a button with
   presence rather than a chip — it matters most in the single-button mode. */
#confirm-dialog-root .cd-btn-ghost{flex:0 0 auto;min-width:98px;white-space:nowrap;padding:10px 19px;border:1.5px solid #dde4ee;background:#fff;color:#475569;font-weight:600}
#confirm-dialog-root .cd-btn-ghost:hover:not(:disabled){border-color:#c3cede;background:#f8fafc;color:#1e293b}
#confirm-dialog-root .cd-btn-ghost:disabled{opacity:.6}
#confirm-dialog-root .cd-btn-primary{flex:0 1 auto;min-width:126px;padding:10px 21px;border:none;background:var(--cd-btn);color:#fff;font-weight:700;text-align:center;box-shadow:0 6px 16px var(--cd-glow),inset 0 1px 0 rgba(255,255,255,.25)}
#confirm-dialog-root .cd-btn-primary:hover:not(:disabled){transform:translateY(-1px);filter:brightness(1.04);box-shadow:0 8px 20px var(--cd-glow),inset 0 1px 0 rgba(255,255,255,.25)}
#confirm-dialog-root .cd-btn-primary:active:not(:disabled){transform:translateY(0);filter:brightness(.98)}
#confirm-dialog-root .cd-btn-primary:disabled{opacity:.65;box-shadow:none}
#confirm-dialog-root .cd-spin{width:13px;height:13px;border-radius:50%;border:2px solid rgba(255,255,255,.45);border-top-color:#fff;display:inline-block;flex-shrink:0;animation:cd-spin .7s linear infinite}
/* Narrow screens, or a confirm label long enough that the pair cannot sit side
   by side: stack them, primary on top, both full width. */
@media (max-width:560px){
  #confirm-dialog-root .cd-hd{padding:16px 16px 14px;gap:12px}
  #confirm-dialog-root .cd-hd .cd-title{font-size:17px}
  #confirm-dialog-root .cd-hdr .cd-body{padding:15px 16px 4px}
}
@media (max-width:430px){
  #confirm-dialog-root .cd-ov{padding:12px}
  #confirm-dialog-root .cd-body{padding:20px 18px 14px}
  #confirm-dialog-root .cd-foot{padding:12px 18px 16px;flex-direction:column-reverse}
  #confirm-dialog-root .cd-btn{width:100%;padding:11px 16px}
  /* column-reverse puts the DOM-first left slot last, i.e. under the buttons —
     which is where an opt-out belongs once the actions are stacked. */
  #confirm-dialog-root .cd-foot-left{margin:6px 0 0;width:100%}
  #confirm-dialog-root .cd-medal-wrap{margin-bottom:12px}
  #confirm-dialog-root .cd-medal{width:46px;height:46px;border-radius:15px}
  #confirm-dialog-root .cd-medal svg{width:22px;height:22px}
  #confirm-dialog-root .cd-title{font-size:16px}
}
@keyframes cd-fade{from{opacity:0}to{opacity:1}}
@keyframes cd-pop{from{opacity:0;transform:translateY(9px) scale(.965)}to{opacity:1;transform:translateY(0) scale(1)}}
@keyframes cd-spin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){
  #confirm-dialog-root .cd-ov,#confirm-dialog-root .cd-card{animation:none}
  #confirm-dialog-root .cd-btn{transition:none}
}
`;
  if (s.textContent === css) return;
  s.textContent = css;
  if (!s.parentNode) document.head.appendChild(s);
}

function ensureRoot() {
  let el = document.getElementById('confirm-dialog-root');
  if (!el) {
    el = document.createElement('div');
    el.id = 'confirm-dialog-root';
    document.body.appendChild(el);
  }
  return el;
}

export default function ConfirmDialog({
  open = false,
  variant = 'warning',
  icon,                       // optional override (emoji string or React node)
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel  = 'Cancel',
  onConfirm,
  onCancel,
  busy = false,               // disables buttons + shows spinner on confirm
  dismissable = true,         // backdrop click / Escape cancels
  hideCancel = false,         // single-button "alert" mode (just an OK / acknowledge)
  align = 'center',           // 'center' for a sentence or two; 'left' for a list / callout
  layout = 'centered',        // 'centered' | 'header' (banded header, full-width body)
  subtitle,                   // header layout only — one line under the title
  showClose = false,          // header layout only — the × in the top-right corner
  footerLeft = null,          // node parked at the footer's left (e.g. an opt-out checkbox)
  size = 'md',                // 'md' (404px) | 'lg' (568px)
}) {
  const rootRef    = useRef(null);
  const confirmRef = useRef(null);
  const cardRef    = useRef(null);
  if (rootRef.current === null && typeof document !== 'undefined') {
    injectStyles();
    rootRef.current = ensureRoot();
  }

  // Scroll lock + autofocus + Escape handling while open.
  useEffect(() => {
    if (!open) return;
    const unlock = lockBodyScroll();
    const t = setTimeout(() => confirmRef.current?.focus(), 30);

    const onKey = (e) => {
      if (e.key === 'Escape' && dismissable && !busy) onCancel?.();
      if (e.key === 'Tab') {
        /* Focus trap across everything focusable, not just buttons — a footer
           slot can hold a checkbox, and a trap that only knows about buttons
           tabs straight past it. */
        const nodes = cardRef.current?.querySelectorAll(
          'button:not(:disabled), input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])'
        );
        const f = nodes ? Array.from(nodes) : [];
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      unlock();
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, busy, dismissable, onCancel]);

  if (!open || !rootRef.current) return null;
  const v = VARIANTS[variant] || VARIANTS.warning;
  const shownIcon = icon ?? v.node;
  const isHeader  = layout === 'header';

  const cardClass = [
    'cd-card',
    align === 'left' ? 'cd-left' : '',
    isHeader ? 'cd-hdr' : '',
    size === 'lg' ? 'cd-lg' : '',
  ].filter(Boolean).join(' ');

  return createPortal(
    <div
      className="cd-ov"
      role="presentation"
      onClick={() => { if (dismissable && !busy) onCancel?.(); }}
    >
      <div
        ref={cardRef}
        className={cardClass}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={title ? 'cd-title' : undefined}
        aria-describedby={message ? 'cd-desc' : undefined}
        onClick={e => e.stopPropagation()}
        /* The only thing that varies per dialog. Everything else is in the sheet. */
        style={{
          '--cd-accent': v.accent,
          '--cd-medal':  v.medallion,
          '--cd-ink':    v.ink,
          '--cd-btn':    v.btn,
          '--cd-glow':   v.btnGlow,
          '--cd-wash':   v.wash || '#f8fafc',
        }}
      >
        {/* Variant accent — tells you the outcome before you read the words. */}
        <div className="cd-accent" aria-hidden="true" />

        {isHeader ? (
          <>
            {/* ── Banded header: medallion + title + subtitle, with the message
                   given the card's full width underneath. ── */}
            <div className="cd-hd">
              <div className="cd-medal" aria-hidden="true">{shownIcon}</div>
              <div className="cd-hd-txt">
                {title    && <h2 id="cd-title" className="cd-title">{title}</h2>}
                {subtitle && <p className="cd-sub">{subtitle}</p>}
              </div>
              {showClose && (
                <button
                  type="button" className="cd-x" aria-label="Close"
                  onClick={() => onCancel?.()} disabled={busy}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                       strokeLinecap="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
            <div className="cd-body">
              {message && <div id="cd-desc" className="cd-desc">{message}</div>}
            </div>
          </>
        ) : (
          /* ── Centred: medallion over title over message, on the card's centre line ── */
          <div className="cd-body">
            <div className="cd-medal-wrap">
              <div className="cd-medal" aria-hidden="true">{shownIcon}</div>
            </div>
            {title   && <h2 id="cd-title" className="cd-title">{title}</h2>}
            {message && <div id="cd-desc" className="cd-desc">{message}</div>}
          </div>
        )}

        {/* ── Footer ── */}
        <div className="cd-foot">
          {footerLeft && <div className="cd-foot-left">{footerLeft}</div>}
          {!hideCancel && (
            <button
              type="button" className="cd-btn cd-btn-ghost"
              onClick={() => onCancel?.()} disabled={busy}
            >
              {cancelLabel}
            </button>
          )}
          <button
            ref={confirmRef} type="button" className="cd-btn cd-btn-primary"
            onClick={() => (onConfirm || onCancel)?.()} disabled={busy}
          >
            {busy && <span className="cd-spin" aria-hidden="true" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    rootRef.current
  );
}
